import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { Loader, Mascot } from "@/components/brand/Mascot";
import { toast } from "sonner";
import { errText } from "@/lib/api";
import { adminMe, adminRetryEmail, getToken, setToken } from "@/lib/admin";

const NAV = [
  ["/admin/dashboard", "Dashboard", "dashboard"], ["/admin/orders", "Orders", "orders"], ["/admin/products", "Products", "products"],
  ["/admin/drops", "Drops", "drops"], ["/admin/bundles", "Bundles", "bundles"], ["/admin/gift-cards", "Gift cards", "gift-cards"], ["/admin/crew-points", "Crew Points", "crew-points"], ["/admin/restock-alerts", "Restock alerts", "restock"], ["/admin/lookbook", "Lookbook", "lookbook"], ["/admin/subscribers", "Subscribers", "subscribers"], ["/admin/sus-of-the-month", "Sus of the Month", "sotm"], ["/admin/settings", "Site settings", "settings"],
];

export const AdminGuard = ({ children }) => {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getToken()) return navigate("/admin/login");
    adminMe().then(setUser).catch(() => { setToken(null); navigate("/admin/login"); });
  }, [navigate]);

  if (!user) return <Loader label="Checking access" />;
  const logout = () => { setToken(null); navigate("/admin/login"); };
  const tab = ({ isActive }) => `label flex items-center justify-between whitespace-nowrap border-l-[3px] px-4 py-3 font-bold transition-colors ${isActive ? "border-ink bg-lime" : "border-transparent text-ink/60 hover:bg-ink/5 hover:text-ink"}`;
  return (
    <div className="mx-auto grid max-w-[1440px] gap-8 px-5 pb-24 pt-8 sm:px-10 lg:grid-cols-[230px_1fr] lg:gap-12" data-testid="admin-shell">
      <Seo title="Admin" description="the suspects admin" path="/admin" noindex />
      <aside className="lg:sticky lg:top-40 lg:self-start" data-testid="admin-sidebar">
        <div className="flex items-center gap-3 border-b border-ink/10 pb-5"><Mascot size={30} label="" /><span className="label font-bold">Admin</span></div>
        <nav className="no-scrollbar -mx-5 mt-4 flex overflow-x-auto px-5 lg:mx-0 lg:block lg:px-0" aria-label="Admin">
          {NAV.map(([to, label, id]) => <NavLink key={to} to={to} className={tab} data-testid={`admin-nav-${id}`}>{label}</NavLink>)}
        </nav>
        <div className="mt-6 hidden border-t border-ink/10 pt-5 lg:block">
          <p className="label truncate !text-[10px] text-ink/60" data-testid="admin-user-email">{user.email}</p>
          <button onClick={logout} className="link-u mt-3" data-testid="admin-logout">Log out</button>
        </div>
      </aside>
      <div className="min-w-0">
        <div className="flex justify-end lg:hidden"><button onClick={logout} className="link-u" data-testid="admin-logout-mobile">Log out</button></div>
        {children}
      </div>
    </div>
  );
};

export const AdminTitle = ({ children, right }) => (
  <div className="flex flex-wrap items-end justify-between gap-6 border-b border-ink/10 pb-6">
    <h1 className="display text-[clamp(3rem,6vw,4.5rem)]">{children}</h1>
    {right}
  </div>
);

const RetryBtn = ({ id }) => {
  const [state, setState] = useState("idle");
  const go = async () => {
    setState("busy");
    try { const r = await adminRetryEmail(id); setState(r.status); toast[r.status === "sent" ? "success" : "error"](r.status === "sent" ? "Email sent" : `Still failing: ${r.error}`); } catch (e) { setState("idle"); toast.error(errText(e)); }
  };
  if (state === "sent") return <span className="label !text-[10px] font-bold">Resent</span>;
  return <button onClick={go} disabled={state === "busy"} className="link-u !text-[10px]" data-testid={`email-retry-${id}`}>{state === "busy" ? "Sending…" : "Retry"}</button>;
};

export const EmailList = ({ emails, testid }) => (
  <ul className="space-y-2" data-testid={testid}>
    {!emails?.length && <li className="text-sm text-ink/60">No emails yet.</li>}
    {emails?.filter((m) => m.status !== "retried").map((m, i) => (
      <li key={i} className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-ink/10 pb-2 text-[13px]">
        <span className={`sticker ${m.status === "sent" ? "bg-lime" : "bg-pink"}`}>{m.status}</span>
        <span className="font-mono text-xs">{m.kind}</span>
        <span>{m.to}{m.cc?.length ? ` (cc ${m.cc.join(", ")})` : ""}</span>
        <span className="font-mono text-[11px] text-ink/60">{m.provider_id ? `id ${m.provider_id}` : m.error}</span>
        {m.status === "failed" && m.id && <RetryBtn id={m.id} />}
      </li>
    ))}
  </ul>
);
